'use client'

/* Copy-link anchors on every id'd heading. A port of the artboard's decorate()
   (Article Reader.dc.html :466–486): the article body is server-rendered innerHTML, so
   the anchors are appended after mount rather than rendered in. Clicking one does not
   jump — it puts the section URL in the clipboard, writes the hash to the address bar
   without scrolling, and shows a check for a second. */

import { useEffect } from 'react'

const LINK_ICON =
  '<svg class="lk" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>' +
  '<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>'
const CHECK_ICON =
  '<svg class="ck" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M20 6 9 17l-5-5"></path></svg>'

const HEADINGS =
  '.lgx-article main h2[id], .lgx-article main h3[id], .lgx-article main h4[id], .lgx-article main h5[id], .lgx-article main h6[id]'

export function HeadingAnchors() {
  useEffect(() => {
    const timers = new WeakMap<HTMLAnchorElement, number>()

    const onClick = (e: MouseEvent) => {
      e.preventDefault()
      const link = e.currentTarget as HTMLAnchorElement
      const hash = link.getAttribute('href') ?? ''
      const url = window.location.href.split('#')[0] + hash
      try {
        window.history.replaceState(null, '', hash)
      } catch {
        /* a sandboxed frame can refuse this; copying still works */
      }
      const done = () => {
        link.dataset.copied = '1'
        window.clearTimeout(timers.get(link))
        timers.set(
          link,
          window.setTimeout(() => {
            delete link.dataset.copied
          }, 1000),
        )
      }
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done, done)
      else done()
    }

    const added: HTMLAnchorElement[] = []
    document.querySelectorAll<HTMLElement>(HEADINGS).forEach((el) => {
      if (el.querySelector('.anchor')) return
      const a = document.createElement('a')
      a.className = 'anchor'
      a.href = `#${el.id}`
      a.setAttribute('aria-label', 'Copy link to this section')
      a.innerHTML = LINK_ICON + CHECK_ICON
      a.addEventListener('click', onClick)
      el.appendChild(a)
      added.push(a)
    })

    return () => {
      added.forEach((a) => {
        window.clearTimeout(timers.get(a))
        a.removeEventListener('click', onClick)
        a.remove()
      })
    }
  }, [])

  return null
}
